import { useContext } from "react";
import {
  Provider,
  ButtonContext,
  DatePickerStateContext,
  TimeFieldContext,
  useSlottedContext,
} from "react-aria-components";
import type { PressEvent, TimeValue } from "react-aria";
import { useLocalizedStringFormatter } from "@/hooks";
import { datePickerMessagesStrings } from "../date-picker.messages";

/**
 * DatePickerCustomContext - Custom context provider for DatePicker
 *
 * Provides React Aria context overrides for button and time input slots,
 * enabling coordinated behavior between DatePicker parts.
 */
export const DatePickerCustomContext = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const msg = useLocalizedStringFormatter(datePickerMessagesStrings);
  const buttonContext = useSlottedContext(ButtonContext) || {};
  const datePickerState = useContext(DatePickerStateContext);
  const noInputValue = datePickerState?.dateValue === null;

  const { timeValue, setTimeValue, granularity } = datePickerState!;

  // Try to get disabled state from the button context
  const isDatePickerDisabled = buttonContext?.isDisabled;

  // Generate default aria-label based on granularity if not provided
  const getDefaultTimeInputAriaLabel = () => {
    switch (granularity) {
      case "hour":
        return msg.format("Time.enterTimeHour");
      case "minute":
        return msg.format("Time.enterTimeHourMinute");
      case "second":
        return msg.format("Time.enterTimeHourMinuteSecond");
      default:
        return msg.format("Time.enterTime");
    }
  };

  /**
   * Button slots
   * ================================
   */
  const buttonSlots = {
    /** toggles the calendar popover */
    calendarToggle: {
      ...buttonContext,
      onPress: (event: PressEvent) => {
        // Ensure any active input (e.g., date picker segment) loses focus
        // because blurring the input will close the popover if it's open (or was just opened)
        const activeElement = document?.activeElement as HTMLElement | null;

        if (activeElement) {
          activeElement.blur();
        }

        buttonContext.onPress?.(event);
      },
    },
    /** Clear button that displays when there's a value in each segment - hidden from both visual and screen readers when there's no value */
    clear: {
      onPress: () => datePickerState?.setValue(null),
      "aria-label": msg.format("clearInput"),
      isDisabled: isDatePickerDisabled,
      // Hide the button when there's no value
      style: noInputValue ? { display: "none" } : undefined,
      "aria-hidden": noInputValue ? true : undefined,
    },
  };

  /**
   * TimeInput slots
   * ================================
   */

  const timeInputSlots = {
    timeInput: {
      value: timeValue,
      onChange: (value: TimeValue | null) => {
        if (value !== null) {
          setTimeValue(value);
        }
      },
      granularity: granularity === "day" ? undefined : granularity,
      "aria-label": getDefaultTimeInputAriaLabel(),
    },
  };

  return (
    <Provider
      values={[
        [
          ButtonContext,
          {
            slots: buttonSlots,
          },
        ],
        [TimeFieldContext, { slots: timeInputSlots }],
      ]}
    >
      {children}
    </Provider>
  );
};
